import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { History as HistoryIcon, Loader2, Download, RotateCcw, Trash2, TrendingUp, TrendingDown, Minus, Link2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import PfsShareDialog from "./PfsShareDialog";

const fmtUSD = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

/**
 * Read-only viewer for a user's historical PFS snapshots.
 *
 * Snapshots are auto-created on every material PUT (money or schedule
 * change) on the backend (`pfs_history` collection). This dialog lets the
 * user browse the timeline, re-export any snapshot as a frozen PDF, or
 * restore one as the current PFS (which auto-snapshots the live state under
 * label="pre-restore" so the action is reversible).
 *
 * Props:
 *   open, onOpenChange — Radix Dialog control
 *   onRestored        — called after a successful restore so the parent
 *                       page can re-fetch /api/pfs and rerender.
 */
export default function PfsHistoryDialog({ open, onOpenChange, onRestored }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [shareSnapshot, setShareSnapshot] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pfs/history");
      setItems(r.data?.items || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load history");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const downloadSnapshot = async (snap) => {
    setBusyId(snap.snapshot_id);
    try {
      await authenticatedDownload(
        `/api/pfs/history/${snap.snapshot_id}/pdf`,
        `pfs-snapshot-${snap.as_of_date || "snap"}-${snap.snapshot_id.slice(0, 8)}.pdf`,
      );
    } catch {
      toast.error("Snapshot PDF download failed");
    } finally {
      setBusyId(null);
    }
  };

  const restore = async (snap) => {
    if (!window.confirm(
      `Restore PFS to ${new Date(snap.snapshotted_at).toLocaleString()} ` +
      `(net worth ${fmtUSD(snap.totals?.net_worth)})?\n\n` +
      `Your current PFS will be auto-snapshotted as a "pre-restore" entry, so you can undo this.`
    )) return;
    setBusyId(snap.snapshot_id);
    try {
      await api.post(`/pfs/history/${snap.snapshot_id}/restore`);
      toast.success("PFS restored from snapshot");
      onRestored?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Restore failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (snap) => {
    if (!window.confirm(`Delete this snapshot? This cannot be undone.`)) return;
    setBusyId(snap.snapshot_id);
    try {
      await api.delete(`/pfs/history/${snap.snapshot_id}`);
      setItems((prev) => prev.filter((s) => s.snapshot_id !== snap.snapshot_id));
      toast.success("Snapshot deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    } finally {
      setBusyId(null);
    }
  };

  const renderDelta = (delta) => {
    if (delta === null || delta === undefined) {
      return (
        <span className="inline-flex items-center gap-1 text-slate-400 text-[11px] font-mono">
          <Minus className="w-3 h-3" /> first
        </span>
      );
    }
    if (delta === 0) {
      return <span className="text-slate-400 text-[11px] font-mono">no change</span>;
    }
    const Icon = delta > 0 ? TrendingUp : TrendingDown;
    const tone = delta > 0 ? "text-emerald-700" : "text-rose-700";
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${tone}`}>
        <Icon className="w-3 h-3" />
        {(delta > 0 ? "+" : "") + fmtUSD(delta)}
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="pfs-history-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <HistoryIcon className="w-5 h-5 text-slate-700" /> Version history
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Every material edit auto-snapshots the prior version. Re-export any snapshot
            as a PDF or restore it as your current PFS (the current version is also
            snapshotted before a restore so you can undo).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500 text-sm">
            No snapshots yet. Save a change to start the timeline.
          </div>
        ) : (
          <ul className="divide-y divide-slate-200" data-testid="pfs-history-list">
            {items.map((s, idx) => (
              <li key={s.snapshot_id} className="py-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center" data-testid={`pfs-history-row-${idx}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900">
                      {new Date(s.snapshotted_at).toLocaleString()}
                    </span>
                    {s.label === "pre-restore" && (
                      <span className="text-[10px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded-sm bg-amber-50 border border-amber-200 text-amber-900">
                        pre-restore
                      </span>
                    )}
                    {s.as_of_date && (
                      <span className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
                        as of {s.as_of_date}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-0.5 font-mono tabular-nums">
                    Assets {fmtUSD(s.totals?.total_assets)} &middot;
                    Liab {fmtUSD(s.totals?.total_liabilities)} &middot;
                    <span className="font-bold text-slate-900"> NW {fmtUSD(s.totals?.net_worth)}</span>
                    <span className="ml-2">{renderDelta(s.net_worth_delta)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 justify-end shrink-0">
                  <button
                    type="button"
                    onClick={() => downloadSnapshot(s)}
                    disabled={busyId === s.snapshot_id}
                    className="text-xs text-slate-700 hover:text-slate-900 px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                    title="Download this snapshot as PDF"
                    data-testid={`pfs-history-download-${idx}`}
                  >
                    {busyId === s.snapshot_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareSnapshot({ id: s.snapshot_id, label: new Date(s.snapshotted_at).toLocaleDateString() })}
                    className="text-xs text-emerald-700 hover:text-emerald-900 px-2 py-1 inline-flex items-center gap-1"
                    title="Generate a lender-shareable read-only link"
                    data-testid={`pfs-history-share-${idx}`}
                  >
                    <Link2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <button
                    type="button"
                    onClick={() => restore(s)}
                    disabled={busyId === s.snapshot_id}
                    className="text-xs text-slate-700 hover:text-slate-900 px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                    title="Restore this snapshot as the current PFS"
                    data-testid={`pfs-history-restore-${idx}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restore
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(s)}
                    disabled={busyId === s.snapshot_id}
                    className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-60"
                    title="Delete snapshot"
                    data-testid={`pfs-history-delete-${idx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
      <PfsShareDialog
        open={!!shareSnapshot}
        onOpenChange={(o) => { if (!o) setShareSnapshot(null); }}
        snapshotId={shareSnapshot?.id}
        snapshotLabel={shareSnapshot?.label}
      />
    </Dialog>
  );
}
